const MATRIX_TOOLTIP_TRIGGER_CLASS = "matrix-tooltip-trigger";
const MATRIX_HORIZONTAL_CLASS = "matrix-layout-horizontal";

export const shouldUseMatrixTooltips = (questionEl) => {
  if (!questionEl) return false;

  const table = questionEl.querySelector(".sd-table");
  const headerRow = table?.querySelector("thead tr");

  if (!table || !headerRow) return false;

  const isHorizontalMatrix = table.classList.contains("sd-table--columnsautowidth");

  if (!isHorizontalMatrix) return false;

  const visibleHeaders = Array.from(headerRow.querySelectorAll("th"))
    .map((th) => (th.textContent || "").replace(/\s+/g, " ").trim())
    .filter(Boolean);

  return visibleHeaders.length > 1;
};

const unwrapTrigger = (trigger) => {
  const parent = trigger.parentNode;
  if (!parent) return;

  while (trigger.firstChild) {
    parent.insertBefore(trigger.firstChild, trigger);
  }

  trigger.remove();
};

const clearQuestionMatrixTooltips = (questionEl) => {
  if (!questionEl) return;

  questionEl.querySelectorAll(`.${MATRIX_TOOLTIP_TRIGGER_CLASS}`).forEach(unwrapTrigger);

  questionEl.querySelectorAll('input[type="radio"]').forEach((radio) => {
    radio.removeAttribute("title");
    radio.removeAttribute("aria-label");
  });
};

export const removeMatrixRadioTooltips = (root) => {
  if (!root) return;

  root.querySelectorAll(`.${MATRIX_TOOLTIP_TRIGGER_CLASS}`).forEach(unwrapTrigger);

  root.querySelectorAll(".sd-question--table").forEach((questionEl) => {
    questionEl.classList.remove(MATRIX_HORIZONTAL_CLASS);
  });

  root.querySelectorAll('.sd-question--table input[type="radio"]').forEach((radio) => {
    radio.removeAttribute("title");
    radio.removeAttribute("aria-label");
  });
};

const getHeaderCellsWithPosition = (table) => {
  return Array.from(table.querySelectorAll("thead th"))
    .map((th) => {
      const text = (th.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return null;

      const rect = th.getBoundingClientRect();

      return {
        text,
        centerX: rect.left + rect.width / 2,
      };
    })
    .filter(Boolean);
};

const getClosestHeaderLabel = (cell, headerCells) => {
  const rect = cell.getBoundingClientRect();
  const cellCenterX = rect.left + rect.width / 2;

  let closestHeader = null;
  let minDistance = Number.POSITIVE_INFINITY;

  headerCells.forEach((header) => {
    const distance = Math.abs(header.centerX - cellCenterX);

    if (distance < minDistance) {
      minDistance = distance;
      closestHeader = header;
    }
  });

  return closestHeader?.text || null;
};

const getDecoratorNode = (cell, radioInput) => {
  return cell.querySelector(".sd-item__decorator") || cell.querySelector(".sd-item__control") || radioInput.parentElement;
};

const applyTooltipToCell = (cell, optionLabel) => {
  const radioInput = cell.querySelector('input[type="radio"]');
  if (!radioInput || !optionLabel) return;

  radioInput.setAttribute("title", optionLabel);
  radioInput.setAttribute("aria-label", optionLabel);

  let trigger = cell.querySelector(`.${MATRIX_TOOLTIP_TRIGGER_CLASS}`);

  if (!trigger) {
    trigger = document.createElement("span");
    trigger.className = MATRIX_TOOLTIP_TRIGGER_CLASS;
    trigger.setAttribute("data-tooltip", optionLabel);

    const decorator = getDecoratorNode(cell, radioInput);
    if (!decorator || !decorator.parentNode) return;

    decorator.parentNode.insertBefore(trigger, decorator);
    trigger.appendChild(decorator);
  } else {
    trigger.setAttribute("data-tooltip", optionLabel);
  }
};

export const enhanceMatrixRadioTooltips = (root) => {
  if (!root) return;

  const matrixQuestions = root.querySelectorAll(".sd-question--table");

  matrixQuestions.forEach((questionEl) => {
    const isHorizontalMatrix = shouldUseMatrixTooltips(questionEl);

    questionEl.classList.toggle(MATRIX_HORIZONTAL_CLASS, isHorizontalMatrix);

    if (!isHorizontalMatrix) {
      clearQuestionMatrixTooltips(questionEl);
      return;
    }

    const table = questionEl.querySelector(".sd-table");
    if (!table) return;

    const headerCells = getHeaderCellsWithPosition(table);
    if (!headerCells.length) return;

    const bodyRows = Array.from(table.querySelectorAll("tbody tr"));

    bodyRows.forEach((row) => {
      const cellsWithRadio = Array.from(row.querySelectorAll("td")).filter((cell) => cell.querySelector('input[type="radio"]'));

      cellsWithRadio.forEach((cell) => {
        const optionLabel = getClosestHeaderLabel(cell, headerCells);
        applyTooltipToCell(cell, optionLabel);
      });
    });
  });
};

export const closeOpenMatrixTooltips = (exceptElement = null) => {
  document.querySelectorAll(`.${MATRIX_TOOLTIP_TRIGGER_CLASS}.is-open`).forEach((el) => {
    if (el !== exceptElement) el.classList.remove("is-open");
  });
};

export const toggleTouchMatrixTooltip = (trigger) => {
  if (!trigger) return;

  const isTouchDevice = window.matchMedia("(hover: none)").matches || window.matchMedia("(pointer: coarse)").matches;

  if (!isTouchDevice) return;

  trigger.classList.toggle("is-open");

  window.setTimeout(() => {
    trigger.classList.remove("is-open");
  }, 1800);
};

export const getMatrixTooltipTriggerFromEvent = (event) => {
  return event.target.closest(`.${MATRIX_TOOLTIP_TRIGGER_CLASS}`);
};
